import { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { Badge, Button, Card, EmptyState, notifyError, notifyInfo } from "../../../components/ui";
import { dialogTheme } from "../../../components/ui/dialog";
import { SectionFrame } from "../components/SectionFrame";
import { ConnectWizard } from "../components/ConnectWizard";
import { useConnections, useJobs, useRadarMutation, useRadarStatus } from "../hooks/useRadarCorreo";
import { radarCorreoApi, apiErrorMessage, type Connection, type TermsInfo } from "../services/radarCorreoService";
import { CONNECTION_TONE, PROVIDER_ICON, fmtDateTime } from "../lib/format";

const PROGRESS_KEYS = ["listed", "candidates", "relevant", "extracted", "events", "alerts", "errors"] as const;

function ConnectionCard({ conn, status }: { conn: Connection; status: TermsInfo }) {
  const { t, i18n } = useTranslation("radarCorreo");
  const reading = conn.status === "active" && !conn.backfill?.done;
  const jobs = useJobs(conn.id, conn.status !== "revoked");
  const job = (jobs.data || [])[0];
  const act = useRadarMutation(async (fn: () => Promise<unknown>) => fn());

  const run = (fn: () => Promise<unknown>) =>
    act.mutate(fn, { onError: (err) => void notifyError(t("common.error"), apiErrorMessage(err) || undefined) });

  const disconnect = async () => {
    const res = await Swal.fire({
      ...dialogTheme("danger"),
      title: t("connection.disconnectTitle"),
      text: t("connection.disconnectText"),
      icon: "warning",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: t("connection.disconnectKeep"),
      denyButtonText: t("connection.disconnectDelete"),
      cancelButtonText: t("common.cancel"),
    });
    if (!res.isConfirmed && !res.isDenied) return;
    run(async () => {
      const out = await radarCorreoApi.disconnect(conn.id, res.isDenied);
      if (out.revokeHint) void notifyInfo(t("connection.revokeHint", { url: out.revokeHint }));
    });
  };

  const months = conn.consent?.backfillMonths;
  const windowLabel = months === 0 ? t("wizard.all") : t("wizard.months", { count: months ?? status.defaultBackfillMonths });

  return (
    <div data-testid={`radar-connection-${conn.id}`}>
    <Card className="space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <i className={`${PROVIDER_ICON[conn.provider]} text-3xl text-primary-500`} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-content">{conn.address || t(`connection.provider.${conn.provider}`)}</p>
          <p className="text-xs text-content-secondary">
            {conn.mine ? t("connection.mine") : t("connection.ownerOf", { name: conn.ownerName || conn.ownerUserId })} · {t(`connection.provider.${conn.provider}`)} ·{" "}
            {t("connection.window", { label: windowLabel })}
          </p>
        </div>
        <Badge tone={CONNECTION_TONE[conn.status]} dot>
          {t(`connection.status.${conn.status}`)}
        </Badge>
      </div>

      {conn.status === "active" && (
        <p className="text-xs text-content-secondary">
          {reading ? t("connection.backfillRunning") : t("connection.backfillDone")}
          {conn.lastSyncAt ? ` · ${t("connection.lastSync", { date: fmtDateTime(conn.lastSyncAt, i18n.language) })}` : ""}
        </p>
      )}
      {conn.lastError?.message && <p className="rounded-lg bg-error/10 p-2 text-xs text-error">{conn.lastError.message}</p>}
      {conn.revokeHint && <p className="text-xs text-content-secondary">{t("connection.revokeHint", { url: conn.revokeHint })}</p>}

      {job && conn.status !== "revoked" && (
        <div className="rounded-lg bg-subtle p-3" data-testid="radar-progress">
          <p className="mb-2 text-xs font-semibold text-content">
            {t("connection.progress.title")} · {t(`connection.stage.${job.stage}`, { defaultValue: job.stage })} · {t(`connection.jobStatus.${job.status}`, { defaultValue: job.status })}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {PROGRESS_KEYS.map((k) => (
              <div key={k}>
                <p className="text-[11px] text-content-secondary">{t(`connection.progress.${k}`)}</p>
                <p className="text-sm font-semibold tabular-nums text-content">{(job.counters?.[k] || 0).toLocaleString(i18n.language)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {conn.budget && (
        <p className="text-xs text-content-secondary">{t("connection.cost", { spent: conn.budget.spentUsd.toFixed(2), cap: conn.budget.capUsd })}</p>
      )}

      {conn.status !== "revoked" && (conn.mine || conn.status !== "pending") && (
        <div className="flex flex-wrap gap-2">
          {conn.status === "active" && (
            <>
              <Button size="sm" variant="secondary" icon={<i className="ri-refresh-line" />} onClick={() => run(() => radarCorreoApi.syncNow(conn.id))}>
                {t("connection.actions.sync")}
              </Button>
              <Button size="sm" variant="secondary" icon={<i className="ri-pause-line" />} onClick={() => run(() => radarCorreoApi.pause(conn.id))}>
                {t("connection.actions.pause")}
              </Button>
            </>
          )}
          {["paused", "error", "budget_exceeded"].includes(conn.status) && (
            <Button size="sm" variant="secondary" icon={<i className="ri-play-line" />} onClick={() => run(() => radarCorreoApi.resume(conn.id))}>
              {t("connection.actions.resume")}
            </Button>
          )}
          {conn.mine && !conn.consentCurrent && (
            <Button
              size="sm"
              variant="warningSoft"
              onClick={() => run(() => radarCorreoApi.reconsent(conn.id, { acceptedTerms: true, termsVersion: status.version, backfillMonths: months ?? status.defaultBackfillMonths }))}
            >
              {t("connection.actions.reconsent")}
            </Button>
          )}
          <Button size="sm" variant="dangerSoft" icon={<i className="ri-link-unlink" />} onClick={disconnect}>
            {t("connection.actions.disconnect")}
          </Button>
        </div>
      )}
    </Card>
    </div>
  );
}

export default function ConexionSection() {
  const { t } = useTranslation("radarCorreo");
  const status = useRadarStatus();
  const connections = useConnections();
  const [wizard, setWizard] = useState(false);

  if (status.data && !status.data.providers.enabled) {
    return (
      <SectionFrame title={t("connection.title")}>
        <EmptyState icon="ri-mail-forbid-line" title={t("disabled.title")} description={t("disabled.text")} />
      </SectionFrame>
    );
  }

  const list = (connections.data || []).filter((c) => c.status !== "revoked" || c.mine);
  return (
    <SectionFrame
      title={t("connection.title")}
      subtitle={t("connection.subtitle")}
      loading={status.isLoading || connections.isLoading}
      error={status.error || connections.error}
      onRetry={() => {
        void status.refetch();
        void connections.refetch();
      }}
      actions={
        <Button icon={<i className="ri-mail-add-line" />} onClick={() => setWizard(true)} disabled={!status.data} data-testid="radar-connect">
          {t("connection.connect")}
        </Button>
      }
    >
      {list.length === 0 ? (
        <EmptyState icon="ri-mail-line" title={t("connection.empty")} action={<Button onClick={() => setWizard(true)}>{t("connection.connect")}</Button>} />
      ) : (
        <div className="space-y-3">
          {list.map((c) => status.data && <ConnectionCard key={c.id} conn={c} status={status.data} />)}
        </div>
      )}
      {wizard && status.data && (
        <ConnectWizard
          status={status.data}
          onClose={() => setWizard(false)}
          onConnected={() => {
            setWizard(false);
            void connections.refetch();
          }}
        />
      )}
    </SectionFrame>
  );
}
